import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config/api';
import { 
  ChevronLeft, 
  Maximize2, 
  Minimize2, 
  RefreshCw, 
  CheckCircle2, 
  AlertCircle,
  Play,
  Pause,
  SkipForward,
  List,
  X,
  Clock,
  Award,
  Target
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Progress } from '../components/ui/progress';
const API = `${API_BASE}/api`;

export default function SCORMPlayer() {
  const { packageId } = useParams();
  const [searchParams] = useSearchParams();
  const scoId = searchParams.get('sco');
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [error, setError] = useState(null);
  const [packageData, setPackageData] = useState(null);
  const [launchData, setLaunchData] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [sessionStatus, setSessionStatus] = useState('not_started');
  const [progress, setProgress] = useState({
    completion: 'unknown',
    success: 'unknown',
    score: null,
    time: '00:00:00'
  });
  
  const iframeRef = useRef(null);
  const containerRef = useRef(null);
  const sessionStartTime = useRef(null);
  const sessionId = useRef(null);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadSCORMPackage();
    
    // Store token for SCORM API access
    localStorage.setItem('token', token);
    
    // Listen for SCORM updates from iframe
    const handleSCORMMessage = (event) => {
      if (event.data?.type === 'scorm_update') {
        console.log('SCORM Update received:', event.data);
        
        if (event.data.eventType === 'initialized') {
          sessionId.current = event.data.sessionId;
          setSessionStatus('running');
          toast.info('SCORM session started');
        } else if (event.data.eventType === 'progress') {
          // Update progress state
          setProgress(prev => ({
            ...prev,
            completion: event.data.completion || prev.completion,
            success: event.data.success || prev.success,
            score: event.data.score ? parseFloat(event.data.score) : prev.score
          }));
        } else if (event.data.eventType === 'finished' || event.data.eventType === 'terminated') {
          setSessionStatus('completed');
          setProgress(prev => ({
            ...prev,
            completion: event.data.completion || 'completed',
            success: event.data.success || prev.success,
            score: event.data.score ? parseFloat(event.data.score) : prev.score
          }));
          
          if (event.data.completion === 'completed' || event.data.completion === 'passed') {
            toast.success('SCORM content completed!');
          }
        }
      }
    };
    
    window.addEventListener('message', handleSCORMMessage);
    
    return () => {
      window.removeEventListener('message', handleSCORMMessage);
      // Cleanup: terminate session on unmount
      if (sessionId.current) {
        terminateSession();
      }
    };
  }, [packageId, token]);

  const loadSCORMPackage = async () => {
    try {
      setLoading(true);
      
      // Get package details
      const pkgRes = await axios.get(`${API}/scorm/packages/${packageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPackageData(pkgRes.data);
      
      // Get launch data
      const launchRes = await axios.get(`${API}/scorm/packages/${packageId}/launch`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { sco_id: scoId }
      });
      setLaunchData(launchRes.data);
      
      // Preconnect to the API for faster subsequent requests
      const preconnectLink = document.createElement('link');
      preconnectLink.rel = 'preconnect';
      preconnectLink.href = API_BASE;
      document.head.appendChild(preconnectLink);
      
      setSessionStatus('ready');
    } catch (err) {
      console.error('Failed to load SCORM package:', err);
      setError(err.response?.data?.detail || 'Failed to load SCORM package');
    } finally {
      setLoading(false);
    }
  };

  const launchContent = () => {
    if (!launchData?.launch_url) return;
    
    sessionStartTime.current = Date.now();
    setSessionStatus('running');
    setIframeLoading(true);
    
    // Build the full URL - launch_url is now a full path like /api/scorm/content/{package_id}/content/index_lms.html
    const baseUrl = launchData.launch_url.startsWith('/') 
      ? `${API_BASE}${launchData.launch_url}`
      : `${API_BASE}/api/scorm/content/${packageId}/content/${launchData.launch_url}`;
    // Add token as query param for SCORM content authentication
    const separator = baseUrl.includes('?') ? '&' : '?';
    const launchUrl = `${baseUrl}${separator}token=${encodeURIComponent(token)}`;
    
    if (iframeRef.current) {
      iframeRef.current.src = launchUrl;
    }
  };

  const handleIframeLoad = () => {
    setIframeLoading(false);
  };

  const terminateSession = async () => {
    if (!sessionId.current) return;
    
    try {
      await axios.post(`${API}/scorm/runtime/terminate`, {
        session_id: sessionId.current
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Failed to terminate session:', err);
    }
  };

  const refreshContent = () => {
    if (iframeRef.current) {
      iframeRef.current.src = iframeRef.current.src;
    }
  };

  const toggleFullscreen = async () => {
    if (!containerRef.current) return;
    
    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        await containerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      }
    }
    setIsFullscreen(!isFullscreen);
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Listen for SCORM messages from iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'scorm_update') {
        const { completion, success, score, sessionId: sid } = event.data;
        sessionId.current = sid;
        setProgress(prev => ({
          ...prev,
          completion: completion || prev.completion,
          success: success || prev.success,
          score: score !== undefined ? score : prev.score
        }));
        
        if (completion === 'completed' || success === 'passed') {
          setSessionStatus('completed');
          toast.success('Content completed successfully!');
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Update time display
  useEffect(() => {
    if (sessionStatus !== 'running') return;
    
    const interval = setInterval(() => {
      if (sessionStartTime.current) {
        const elapsed = Math.floor((Date.now() - sessionStartTime.current) / 1000);
        const hours = Math.floor(elapsed / 3600);
        const minutes = Math.floor((elapsed % 3600) / 60);
        const seconds = elapsed % 60;
        setProgress(prev => ({
          ...prev,
          time: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        }));
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [sessionStatus]);

  if (loading) {
    return (
      <div className="min-h-screen bg-navigate-dark flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-navigate-gold mx-auto mb-4"></div>
          <p className="text-zinc-400">Loading SCORM content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-navigate-dark flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Failed to Load Content</h2>
          <p className="text-zinc-400 mb-6">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-navigate-gold text-black rounded-lg font-medium hover:bg-navigate-gold-light transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="min-h-screen bg-navigate-dark flex flex-col"
      data-testid="scorm-player"
    >
      {/* Header */}
      <header className={`h-14 bg-navigate-surface border-b border-zinc-800 flex items-center px-4 gap-4 ${isFullscreen ? 'hidden' : ''}`}>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
        >
          <ChevronLeft className="h-5 w-5" />
          <span className="hidden sm:inline">Back</span>
        </button>
        
        <div className="flex-1 min-w-0">
          <h1 className="text-white font-medium truncate">
            {launchData?.title || packageData?.title || 'SCORM Content'}
          </h1>
          <p className="text-xs text-zinc-500">
            SCORM {packageData?.scorm_version} • {packageData?.scos?.length || 1} SCO(s)
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Status Indicators */}
          <div className="hidden md:flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-zinc-500" />
              <span className="text-zinc-400">{progress.time}</span>
            </div>
            
            {progress.score !== null && (
              <div className="flex items-center gap-1.5">
                <Target className="h-4 w-4 text-zinc-500" />
                <span className="text-zinc-400">{progress.score}%</span>
              </div>
            )}
            
            <div className={`flex items-center gap-1.5 ${
              progress.completion === 'completed' ? 'text-green-400' : 
              progress.success === 'passed' ? 'text-green-400' : 'text-zinc-400'
            }`}>
              {progress.completion === 'completed' || progress.success === 'passed' ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <div className="h-4 w-4 rounded-full border-2 border-current" />
              )}
              <span className="capitalize">{progress.completion}</span>
            </div>
          </div>

          {/* SCO List Toggle */}
          {packageData?.scos?.length > 1 && (
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="p-2 text-zinc-400 hover:text-white transition-colors"
              title="Show content list"
            >
              <List className="h-5 w-5" />
            </button>
          )}

          <button
            onClick={refreshContent}
            className="p-2 text-zinc-400 hover:text-white transition-colors"
            title="Refresh content"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
          
          <button
            onClick={toggleFullscreen}
            className="p-2 text-zinc-400 hover:text-white transition-colors"
            title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isFullscreen ? (
              <Minimize2 className="h-5 w-5" />
            ) : (
              <Maximize2 className="h-5 w-5" />
            )}
          </button>
        </div>
      </header>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* SCORM Content Frame */}
        <main className={`flex-1 relative bg-white ${showSidebar ? 'mr-72' : ''}`}>
          {sessionStatus === 'ready' && (
            <div className="absolute inset-0 flex items-center justify-center bg-navigate-dark">
              <div className="text-center">
                <div className="w-24 h-24 rounded-full bg-navigate-gold/20 flex items-center justify-center mx-auto mb-6">
                  <Play className="h-12 w-12 text-navigate-gold" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  {launchData?.title || 'SCORM Content'}
                </h2>
                <p className="text-zinc-400 mb-6 max-w-md">
                  Click the button below to launch the interactive content. 
                  Your progress will be tracked automatically.
                </p>
                <button
                  onClick={launchContent}
                  className="px-8 py-3 bg-navigate-gold text-black rounded-lg font-semibold hover:bg-navigate-gold-light transition-colors inline-flex items-center gap-2"
                >
                  <Play className="h-5 w-5" />
                  Launch Content
                </button>
              </div>
            </div>
          )}
          
          {/* Loading indicator for iframe */}
          {iframeLoading && sessionStatus === 'running' && (
            <div className="absolute inset-0 bg-navigate-dark flex items-center justify-center z-10">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-navigate-gold border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-white text-lg">Loading content...</p>
                <p className="text-zinc-400 text-sm mt-2">This may take a moment for large files</p>
              </div>
            </div>
          )}
          
          <iframe
            ref={iframeRef}
            onLoad={handleIframeLoad}
            className={`w-full h-full border-0 ${sessionStatus === 'ready' ? 'invisible' : ''}`}
            title="SCORM Content"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
          />
          
          {/* Fullscreen Controls Overlay */}
          {isFullscreen && (
            <div className="absolute top-4 right-4 flex gap-2 opacity-0 hover:opacity-100 transition-opacity">
              <button
                onClick={refreshContent}
                className="p-2 bg-black/70 text-white rounded-lg hover:bg-black/90 transition-colors"
              >
                <RefreshCw className="h-5 w-5" />
              </button>
              <button
                onClick={toggleFullscreen}
                className="p-2 bg-black/70 text-white rounded-lg hover:bg-black/90 transition-colors"
              >
                <Minimize2 className="h-5 w-5" />
              </button>
            </div>
          )}
        </main>

        {/* SCO List Sidebar */}
        {showSidebar && packageData?.scos?.length > 1 && (
          <aside className="w-72 bg-navigate-surface border-l border-zinc-800 overflow-y-auto">
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="font-semibold text-white">Content Items</h3>
              <button
                onClick={() => setShowSidebar(false)}
                className="p-1 text-zinc-400 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            
            <div className="p-2">
              {packageData.scos.map((sco, index) => (
                <button
                  key={sco.sco_id}
                  onClick={() => {
                    navigate(`/scorm/${packageId}?sco=${sco.sco_id}`);
                    window.location.reload();
                  }}
                  className={`w-full text-left p-3 rounded-lg mb-1 transition-colors ${
                    sco.sco_id === (launchData?.sco_id || packageData.scos[0]?.sco_id)
                      ? 'bg-navigate-gold/10 text-navigate-gold'
                      : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-zinc-800 text-xs flex items-center justify-center">
                      {index + 1}
                    </span>
                    <span className="text-sm truncate">{sco.title}</span>
                  </div>
                </button>
              ))}
            </div>
          </aside>
        )}
      </div>

      {/* Footer Progress Bar (Fullscreen) */}
      {isFullscreen && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-zinc-800">
          <div 
            className="h-full bg-navigate-gold transition-all duration-300"
            style={{ 
              width: progress.completion === 'completed' ? '100%' : 
                     progress.score ? `${progress.score}%` : '0%' 
            }}
          />
        </div>
      )}
    </div>
  );
}
